import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut } from 'lucide-react';
import { cartAPI } from '../services/api';
import { useCartSession } from '../hooks/useAPI';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const sessionId = useCartSession();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [cartItemsCount, setCartItemsCount] = useState(0);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/');
  };

  // Load cart count
  useEffect(() => {
    const loadCartCount = async () => {
      try {
        const cartData = await cartAPI.getCart(sessionId);
        setCartItemsCount(cartData.totalItems || 0);
      } catch (error) {
        console.error('Failed to load cart count:', error);
        setCartItemsCount(0);
      }
    };

    loadCartCount();
  }, [sessionId]);

  return (
    <header className="bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-100">
      {/* Sottile gradiente decorativo */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-18" style={{height: '4.5rem'}}>
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="transform group-hover:scale-105 transition-transform duration-300 ease-out">
              {/* Logo normale - nascosto tra 1053px e 1309px */}
              <img 
                src="/logo-il-tuo-amaro.webp" 
                alt="Il Tuo Amaro" 
                className="h-6 md:h-7 w-auto drop-shadow-sm hidden min-[1310px]:block min-[0px]:max-[1052px]:block"
              />
              {/* Favicon - mostrato solo tra 1053px e 1309px */}
              <img 
                src="/favicon.webp" 
                alt="Il Tuo Amaro" 
                className="h-10 w-10 drop-shadow-sm hidden min-[1053px]:max-[1309px]:block"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            <Link
              to="/builder"
              className="px-4 py-2 font-bold text-sm tracking-wide relative group transition-all duration-300 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 bg-clip-text text-transparent hover:from-purple-500 hover:via-pink-400 hover:to-purple-500 animate-gradient"
              style={{
                backgroundSize: '200% auto',
              }}
            >
              PREVENTIVO
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 opacity-60 group-hover:opacity-100 transition-opacity duration-300"></span>
            </Link>
            <Link
              to="/campioni"
              className="px-4 py-2 text-gray-700 hover:text-purple-600 font-semibold text-sm tracking-wide relative group transition-colors duration-300"
            >
              CAMPIONI
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600 group-hover:w-full transition-all duration-300 ease-out"></span>
            </Link>
            <Link
              to="/distilleria"
              className="px-4 py-2 text-gray-700 hover:text-purple-600 font-semibold text-sm tracking-wide relative group transition-colors duration-300"
            >
              DISTILLERIA
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600 group-hover:w-full transition-all duration-300 ease-out"></span>
            </Link>
            <Link
              to="/faq"
              className="px-4 py-2 text-gray-700 hover:text-purple-600 font-semibold text-sm tracking-wide relative group transition-colors duration-300"
            >
              FAQ
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600 group-hover:w-full transition-all duration-300 ease-out"></span>
            </Link>
            <Link
              to="/blog"
              className="px-4 py-2 text-gray-700 hover:text-purple-600 font-semibold text-sm tracking-wide relative group transition-colors duration-300"
            >
              BLOG
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600 group-hover:w-full transition-all duration-300 ease-out"></span>
            </Link>
            <Link
              to="/contatti"
              className="px-4 py-2 text-gray-700 hover:text-purple-600 font-semibold text-sm tracking-wide relative group transition-colors duration-300"
            >
              CONTATTACI
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600 group-hover:w-full transition-all duration-300 ease-out"></span>
            </Link>
          </nav>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-4">
            {/* User Menu */}
            {isAuthenticated() ? (
              <div className="relative">
                <button 
                  className="flex items-center space-x-3 p-2 text-gray-700 hover:text-purple-600 transition-all duration-300 bg-gray-50/50 hover:bg-purple-50 rounded-xl border border-gray-200/50 hover:border-purple-200 backdrop-blur-sm group"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-semibold group-hover:scale-110 transition-transform duration-300">
                    {user?.firstName?.charAt(0)}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">
                    {user?.firstName}
                  </span>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-3 w-56 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 py-2 z-50 animate-in slide-in-from-top-2 duration-200">
                    <Link
                      to="/dashboard"
                      className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 transition-all duration-200 font-medium rounded-lg mx-2"
                      onClick={() => setShowUserMenu(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/dashboard?tab=orders"
                      className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 transition-all duration-200 font-medium rounded-lg mx-2"
                      onClick={() => setShowUserMenu(false)}
                    >
                      📋 Ordini
                    </Link>
                    <Link
                      to="/dashboard?tab=quotes"
                      className="block px-4 py-3 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50/50 transition-all duration-200 font-medium rounded-lg mx-2"
                      onClick={() => setShowUserMenu(false)}
                    >
                      📄 Preventivi
                    </Link>
                    <Link
                      to="/dashboard?tab=invoices"
                      className="block px-4 py-3 text-sm text-gray-700 hover:text-green-600 hover:bg-green-50/50 transition-all duration-200 font-medium rounded-lg mx-2"
                      onClick={() => setShowUserMenu(false)}
                    >
                      🧾 Fatture
                    </Link>
                    <Link
                      to="/dashboard?tab=labels"
                      className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 transition-all duration-200 font-medium rounded-lg mx-2"
                      onClick={() => setShowUserMenu(false)}
                    >
                      📄 Area invio file
                    </Link>
                    <hr className="my-2 border-gray-200/50" />
                    <Link
                      to="/carrello"
                      className="block px-4 py-3 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 transition-all duration-200 font-medium rounded-lg mx-2"
                      onClick={() => setShowUserMenu(false)}
                    >
                      Carrello
                    </Link>
                    <hr className="my-2 border-gray-200/50" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50/50 transition-all duration-200 flex items-center font-medium rounded-lg mx-2"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="hidden md:block px-4 py-2 text-sm font-medium text-gray-600 hover:text-purple-600 transition-colors duration-300"
                >
                  Accedi
                </Link>
                <Link
                  to="/register"
                  className="hidden sm:block px-6 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-sm font-medium rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Registrati
                </Link>
              </div>
            )}
            
            <button 
              className="p-2 text-gray-600 hover:text-purple-600 transition-all duration-300 relative bg-gray-50/50 hover:bg-purple-50 rounded-xl border border-gray-200/50 hover:border-purple-200 group backdrop-blur-sm"
              onClick={() => navigate('/carrello')}
            >
              <ShoppingCart className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center shadow-lg animate-pulse">
                  {cartItemsCount}
                </span>
              )}
            </button>
            
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-gray-600 hover:text-purple-600 transition-all duration-300 bg-gray-50/50 hover:bg-purple-50 rounded-xl border border-gray-200/50 hover:border-purple-200 backdrop-blur-sm"
              onClick={toggleMobileMenu}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 animate-in slide-in-from-top-2 duration-300">
            <nav className="flex flex-col py-6 px-2 space-y-1">
              <Link
                to="/builder"
                className="px-4 py-3 font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 bg-clip-text text-transparent hover:bg-purple-50/50 transition-all duration-200 rounded-xl animate-gradient"
                style={{
                  backgroundSize: '200% auto',
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                PREVENTIVO
              </Link>
              <Link
                to="/campioni"
                className="px-4 py-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 font-medium transition-all duration-200 rounded-xl"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                CAMPIONI
              </Link>
              <Link
                to="/distilleria"
                className="px-4 py-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 font-medium transition-all duration-200 rounded-xl"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                DISTILLERIA
              </Link>
              <Link
                to="/faq"
                className="px-4 py-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 font-medium transition-all duration-200 rounded-xl"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                FAQ
              </Link>
              <Link
                to="/blog"
                className="px-4 py-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 font-medium transition-all duration-200 rounded-xl"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                BLOG
              </Link>
              <Link
                to="/contatti"
                className="px-4 py-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 font-medium transition-all duration-200 rounded-xl"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                CONTATTACI
              </Link>
              
              {/* Mobile Auth Links */}
              <hr className="border-gray-200/50 my-4" />
              {isAuthenticated() ? (
                <>
                  <Link
                    to="/dashboard"
                    className="px-4 py-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 font-medium transition-all duration-200 rounded-xl"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    DASHBOARD
                  </Link>
                  <Link
                    to="/dashboard?tab=orders"
                    className="px-4 py-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 font-medium transition-all duration-200 rounded-xl"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    📋 ORDINI
                  </Link>
                  <Link
                    to="/dashboard?tab=quotes"
                    className="px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50/50 font-medium transition-all duration-200 rounded-xl"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    📄 PREVENTIVI
                  </Link>
                  <Link
                    to="/dashboard?tab=invoices"
                    className="px-4 py-3 text-gray-700 hover:text-green-600 hover:bg-green-50/50 font-medium transition-all duration-200 rounded-xl"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    🧾 FATTURE
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-left px-4 py-3 text-gray-500 hover:text-red-600 hover:bg-red-50/50 font-medium transition-all duration-200 rounded-xl"
                  >
                    LOGOUT
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50/50 font-medium transition-all duration-200 rounded-xl"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    ACCEDI
                  </Link>
                  <Link
                    to="/register/private"
                    className="mx-4 py-3 px-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium transition-all duration-200 rounded-xl text-center shadow-lg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    REGISTRATI
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
      
      {/* Stile per l'animazione del gradiente */}
      <style jsx>{`
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </header>
  );
};

export default Header;